import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, Logger, type Event } from '@patsnap/tahiti-core';
import type { DocProfileWithMatchedTechFeature, PatentProfileWithMatchedTechFeature, RetrievalResult, TechFeatureSynonym } from '../../types';
import { ComplexRetrievalRepository, type ComplexRetrievalSearchOptions, type ComplexRetrievalService, type RetrievalTimeCost } from '../complex-retrieval-service';
import { RetrievalProgressService } from '../retrieval-progress-service';
import { CurrentMatchedPatentModel } from '../../models/current-matched-patent-model';
import { CurrentMatchedPaperModel } from '../../models/current-matched-paper-model';
import { CurrentMatchedWebsiteModel } from '../../models/current-matched-website-model';
import _ from 'lodash';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import type { RETRIEVAL_STRATEGY } from '../../constants';
import { TerminateSearchAgentApi } from '@/infrastructure/api-functions/core-drafting-api/terminate-search-agent-api';
import { RetrievalResultReportModel } from '../../models/retrieval-result-report-model';
import { CommonEvidenceSearchService } from '../common-evidence-search-service';

@injectable()
export class ComplexRetrievalServiceImpl implements ComplexRetrievalService {

    // eslint-disable-next-line max-params
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(RetrievalProgressService)
        private readonly _retrievalProgressService: RetrievalProgressService,
        @inject(CurrentMatchedPatentModel)
        private readonly _currentMatchedPatentModel: CurrentMatchedPatentModel,
        @inject(CurrentMatchedPaperModel)
        private readonly _currentMatchedPaperModel: CurrentMatchedPaperModel,
        @inject(CurrentMatchedWebsiteModel)
        private readonly _currentMatchedWebsiteModel: CurrentMatchedWebsiteModel,
        @inject(ComplexRetrievalRepository)
        private readonly _complexRetrievalRepository: ComplexRetrievalRepository,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(TerminateSearchAgentApi)
        private readonly _terminateSearchAgentApi: TerminateSearchAgentApi,
        @inject(RetrievalResultReportModel)
        private readonly _retrievalResultReportModel: RetrievalResultReportModel,
        @inject(CommonEvidenceSearchService)
        private readonly _commonEvidenceSearchService: CommonEvidenceSearchService,
    ) { }

    private _onRetrievalStartEmitter: Emitter<void> = new Emitter();
    private _onRetrievalRunningEmitter: Emitter<[RETRIEVAL_STRATEGY, boolean]> = new Emitter();
    private _onRetrievalCompleteEmitter: Emitter<void> = new Emitter();
    private _onPatentFeatureMatchedEmitter: Emitter<PatentProfileWithMatchedTechFeature[]> = new Emitter();
    private _onPaperFeatureMatchedEmitter: Emitter<DocProfileWithMatchedTechFeature[]> = new Emitter();
    private _onWebsiteFeatureMatchedEmitter: Emitter<DocProfileWithMatchedTechFeature[]> = new Emitter();
    private _onTechFeatureSynonymEmitter: Emitter<TechFeatureSynonym[]> = new Emitter();
    private _onRetrievalTimeEmitter: Emitter<RetrievalTimeCost> = new Emitter();

    readonly onRetrievalStart: Event<void> = this._onRetrievalStartEmitter.event;
    readonly onRetrievalRunning: Event<[RETRIEVAL_STRATEGY, boolean]> = this._onRetrievalRunningEmitter.event;
    readonly onRetrievalComplete: Event<void> = this._onRetrievalCompleteEmitter.event;
    readonly onPatentFeatureMatched: Event<PatentProfileWithMatchedTechFeature[]> = this._onPatentFeatureMatchedEmitter.event;
    readonly onPaperFeatureMatched: Event<DocProfileWithMatchedTechFeature[]> = this._onPaperFeatureMatchedEmitter.event;
    readonly onWebsiteFeatureMatched: Event<DocProfileWithMatchedTechFeature[]> = this._onWebsiteFeatureMatchedEmitter.event;
    readonly onTechFeatureSynonym: Event<TechFeatureSynonym[]> = this._onTechFeatureSynonymEmitter.event;
    readonly onRetrievalTime: Event<RetrievalTimeCost> = this._onRetrievalTimeEmitter.event;

    async terminate(): Promise<void> {
        // 触发检索完成事件
        this._onRetrievalCompleteEmitter.fire();
        // 终止当前的检索（循环）
        await this._complexRetrievalRepository.terminate();
        this._retrievalProgressService.setAllResultFinished();
        // 将检索中的策略进度设置为 false
        this._setRetrievalProgressToFalse();
        // 发送终止搜索请求的请求
        return this._terminateRepository();
    }

    // 将检索中的策略进度设置为 false
    private _setRetrievalProgressToFalse() {
        const runningStrategies = this._retrievalProgressService.getRunningStrategies();
        runningStrategies.forEach((strategy) => {
            this._retrievalProgressService.add(strategy, false);
        });
    }

    private _terminateRepository() {
        return this._terminateSearchAgentApi.terminateSearchAgent({
            task_id: this._currentTaskService.taskId,
        });
    }

    async search(options?: ComplexRetrievalSearchOptions): Promise<RetrievalResult> {
        const taskId = this._currentTaskService.taskId;
        const isSharedTask = taskId.startsWith('sh_');
        const startSearch = !isSharedTask && (options?.startSearch ?? true);
        this._logger.info('复杂检索服务开始检索', taskId);

        if (startSearch) {
            // 预热拆解特征
            this._preheatFeatureExtraction();
        }

        this._onRetrievalStartEmitter.fire();
        await this._complexRetrievalRepository.search(taskId, {
            onRetrievalRunning: (name, running) => this._retrievalProgressService.add(name, running),
            onRetrievalSuccess: (name, result) => this._retrievalProgressService.updateResult(name, result),
            onPatentFeatureMatched: (patents) => this._firePatentFeatureMatched(patents),
            onPaperFeatureMatched: (papers) => this._firePaperFeatureMatched(papers),
            onWebsiteFeatureMatched: (websites) => this._fireWebsiteFeatureMatched(websites),
            onTechFeatureSynonym: (synonyms) => this._onTechFeatureSynonymEmitter.fire(synonyms),
            onRetrievalTime: (time) => this._onRetrievalTimeEmitter.fire(time),
            onGetSearchElement: (elements,familyMergeEnabled) => this._retrievalResultReportModel.update((state) => ({
                ...state,
                elements,
                familyMergeEnabled,
            })),
        }, {
            startSearch,
        });
        this._onRetrievalCompleteEmitter.fire();
        return {
            idea: '',
            searchExpress: '',
            patents: [],
            strategyParameters: [],
            nextPlan: '',
            finished: false,
        };
    }

    /**
     * 预热拆解特征
     * @description 调用常见证据检索服务的预热方法，为后续检索做准备
     */
    private async _preheatFeatureExtraction(): Promise<void> {
        try {
            this._logger.info('开始预热特征拆解');
            await this._commonEvidenceSearchService.preheatFeatureExtraction();
            this._logger.info('特征拆解预热完成');
        }
        catch (error) {
            // 预热失败不应阻塞检索流程，只记录警告
            this._logger.warn('特征拆解预热失败，将继续检索流程', {
                error: error instanceof Error ? {
                    message: error.message,
                    stack: error.stack,
                } : String(error),
            });
        }
    }

    /**
     * 触发专利有匹配特征事件
     * @param techFeatures 匹配特征
     */
    private _firePatentFeatureMatched(techFeatures: PatentProfileWithMatchedTechFeature[]) {
        this._onPatentFeatureMatchedEmitter.fire(techFeatures);
        this._currentMatchedPatentModel.update((state) => ({
            ...state,
            ranking: _.uniqBy([...techFeatures, ...state.ranking], 'patentId'),
        }));
    }

    /**
     * 触发论文有匹配特征事件
     * @param papers 匹配特征
     */
    private _firePaperFeatureMatched(papers: DocProfileWithMatchedTechFeature[]) {
        this._onPaperFeatureMatchedEmitter.fire(papers);
        this._currentMatchedPaperModel.update((state) => ({
            ...state,
            ranking: _.uniqBy([...papers, ...state.ranking], 'docId'),
        }));
    }

    /**
     * 触发网站有匹配特征事件
     * @param websites 匹配特征
     */
    private _fireWebsiteFeatureMatched(websites: DocProfileWithMatchedTechFeature[]) {
        this._onWebsiteFeatureMatchedEmitter.fire(websites);
        this._currentMatchedWebsiteModel.update((state) => ({
            ...state,
            ranking: _.uniqBy([...websites, ...state.ranking], 'docId'),
        }));
    }
}
